// src/core/config/firebase.ts

// Importa los tipos y funciones necesarios del SDK de Firebase
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { 
  initializeFirestore, 
  type Firestore, 
  Timestamp, 
  FieldValue, 
  persistentLocalCache, 
  persistentMultipleTabManager 
} from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getAnalytics, type Analytics } from "firebase/analytics";
import { getMessaging, type Messaging, isSupported } from "firebase/messaging";
import { getFunctions, type Functions } from "firebase/functions";

// Define el tipo para la configuración de Firebase para mayor seguridad y autocompletado
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

// Exporta las instancias de los servicios con tipado explícito para ser usadas en toda la aplicación.
export const auth: Auth = getAuth(app);
export const db: Firestore = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
    })
});
export const storage: FirebaseStorage = getStorage(app);
export const analytics: Analytics = getAnalytics(app);
export const functions: Functions = getFunctions(app);

// Inicializar FCM de forma condicional (solo si está soportado)
let messaging: Messaging | null = null;
const initializeMessaging = async (): Promise<Messaging | null> => {
  try {
    const supported = await isSupported();
    if (supported) {
      messaging = getMessaging(app);
      return messaging;
    }
    return null;
  } catch (error) {
    console.warn('FCM no soportado en este navegador:', error);
    return null;
  }
};

export { messaging, initializeMessaging };
export { Timestamp, FieldValue };
